import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bell,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  Clock,
  Zap,
  Eye,
  EyeOff,
  MoreHorizontal,
} from "lucide-react";
import { useAlerts } from "@/hooks/useAlerts";
import type { Alert } from "@/services/alert.service";

interface AlertsCenterProps {
  className?: string;
  compact?: boolean;
  showOnlyUnread?: boolean;
  maxItems?: number;
}

const AlertsCenter: React.FC<AlertsCenterProps> = ({
  className = "",
  compact = false,
  showOnlyUnread = false,
  maxItems = 10,
}) => {
  const {
    alerts,
    unreadAlerts,
    criticalAlerts,
    stats,
    markAsRead,
    markAsResolved,
    executeAction,
    isLoading,
  } = useAlerts();

  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedSeverity, setSelectedSeverity] = useState<string>("all");
  const [showResolved, setShowResolved] = useState(false);

  // Filtrar alertas baseado nos critérios
  const filteredAlerts = alerts
    .filter((alert) => {
      if (showOnlyUnread && alert.isRead) return false;
      if (!showResolved && alert.isResolved) return false;
      if (selectedCategory !== "all" && alert.category !== selectedCategory)
        return false;
      if (selectedSeverity !== "all" && alert.severity !== selectedSeverity)
        return false;
      return true;
    })
    .slice(0, maxItems);

  const getAlertIcon = (severity: Alert["severity"]) => {
    switch (severity) {
      case "critical":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case "info":
        return <Info className="h-4 w-4 text-blue-600" />;
    }
  };

  const getAlertBadgeColor = (severity: Alert["severity"]) => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "info":
        return "bg-blue-100 text-blue-800";
    }
  };

  const getCategoryIcon = (category: Alert["category"]) => {
    switch (category) {
      case "connection":
        return "🔌";
      case "data":
        return "📊";
      case "performance":
        return "⚡";
      case "sync":
        return "🔄";
      case "security":
        return "🔒";
      default:
        return "📋";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Agora mesmo";
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;

    return date.toLocaleDateString("pt-BR");
  };

  const handleMarkAsRead = (alertId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    markAsRead(alertId);
  };

  const handleMarkAsResolved = (alertId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    markAsResolved(alertId);
  };

  const handleExecuteAction = async (
    alertId: string,
    actionId: string,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    try {
      await executeAction(alertId, actionId);
    } catch (error) {
      console.error("Erro ao executar ação:", error);
    }
  };

  if (compact) {
    // Versão compacta para sidebar
    return (
      <Card
        className={`${className} ${criticalAlerts.length > 0 ? "border-red-200 bg-red-50" : ""}`}
      >
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell
                className={`h-4 w-4 ${criticalAlerts.length > 0 ? "text-red-600" : "text-muted-foreground"}`}
              />
              <span className="text-sm font-medium">Alertas</span>
              {stats.unread > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {stats.unread}
                </Badge>
              )}
            </div>
            {criticalAlerts.length > 0 && (
              <Badge variant="destructive" className="animate-pulse">
                {criticalAlerts.length} crítico(s)
              </Badge>
            )}
          </div>

          {criticalAlerts.length > 0 && (
            <div className="mt-3 space-y-2">
              {criticalAlerts.slice(0, 2).map((alert) => (
                <div
                  key={alert.id}
                  className="text-xs p-2 bg-white border border-red-200 rounded"
                >
                  <div className="font-medium text-red-800">{alert.title}</div>
                  <div className="text-red-600 mt-1">
                    {alert.integrationName}
                  </div>
                </div>
              ))}
              {criticalAlerts.length > 2 && (
                <div className="text-xs text-red-600 text-center">
                  +{criticalAlerts.length - 2} mais...
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Versão completa
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Centro de Alertas
              {stats.unread > 0 && (
                <Badge variant="destructive">{stats.unread} não lidos</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Monitoramento em tempo real de problemas nas integrações
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowResolved(!showResolved)}
            >
              {showResolved ? (
                <EyeOff className="h-3 w-3" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
              {showResolved ? "Ocultar resolvidos" : "Mostrar resolvidos"}
            </Button>
          </div>
        </div>

        {/* Estatísticas rápidas */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {stats.critical}
            </div>
            <div className="text-xs text-red-800">Críticos</div>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {stats.bySeverity.warning || 0}
            </div>
            <div className="text-xs text-yellow-800">Avisos</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {stats.bySeverity.info || 0}
            </div>
            <div className="text-xs text-blue-800">Informativos</div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="all">Todos ({stats.total})</TabsTrigger>
            <TabsTrigger value="critical">
              Críticos ({stats.critical})
            </TabsTrigger>
            <TabsTrigger value="unread">Não lidos ({stats.unread})</TabsTrigger>
            <TabsTrigger value="filters">Filtros</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            <AlertsList
              alerts={filteredAlerts}
              onMarkAsRead={handleMarkAsRead}
              onMarkAsResolved={handleMarkAsResolved}
              onExecuteAction={handleExecuteAction}
              isLoading={isLoading}
            />
          </TabsContent>

          <TabsContent value="critical" className="space-y-4">
            <AlertsList
              alerts={criticalAlerts}
              onMarkAsRead={handleMarkAsRead}
              onMarkAsResolved={handleMarkAsResolved}
              onExecuteAction={handleExecuteAction}
              isLoading={isLoading}
            />
          </TabsContent>

          <TabsContent value="unread" className="space-y-4">
            <AlertsList
              alerts={unreadAlerts}
              onMarkAsRead={handleMarkAsRead}
              onMarkAsResolved={handleMarkAsResolved}
              onExecuteAction={handleExecuteAction}
              isLoading={isLoading}
            />
          </TabsContent>

          <TabsContent value="filters" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Categoria</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="all">Todas</option>
                  <option value="connection">Conexão</option>
                  <option value="data">Dados</option>
                  <option value="performance">Performance</option>
                  <option value="sync">Sincronização</option>
                  <option value="security">Segurança</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Severidade</label>
                <select
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="all">Todas</option>
                  <option value="critical">Crítico</option>
                  <option value="warning">Aviso</option>
                  <option value="info">Informativo</option>
                </select>
              </div>
            </div>

            <AlertsList
              alerts={filteredAlerts}
              onMarkAsRead={handleMarkAsRead}
              onMarkAsResolved={handleMarkAsResolved}
              onExecuteAction={handleExecuteAction}
              isLoading={isLoading}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

// Componente separado para a lista de alertas
interface AlertsListProps {
  alerts: Alert[];
  onMarkAsRead: (alertId: string, event: React.MouseEvent) => void;
  onMarkAsResolved: (alertId: string, event: React.MouseEvent) => void;
  onExecuteAction: (
    alertId: string,
    actionId: string,
    event: React.MouseEvent,
  ) => Promise<void>;
  isLoading: boolean;
}

const AlertsList: React.FC<AlertsListProps> = ({
  alerts,
  onMarkAsRead,
  onMarkAsResolved,
  onExecuteAction,
  isLoading,
}) => {
  const getAlertIcon = (severity: Alert["severity"]) => {
    switch (severity) {
      case "critical":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case "info":
        return <Info className="h-4 w-4 text-blue-600" />;
    }
  };

  const getAlertBadgeColor = (severity: Alert["severity"]) => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "info":
        return "bg-blue-100 text-blue-800";
    }
  };

  const getCategoryIcon = (category: Alert["category"]) => {
    switch (category) {
      case "connection":
        return "🔌";
      case "data":
        return "📊";
      case "performance":
        return "⚡";
      case "sync":
        return "🔄";
      case "security":
        return "🔒";
      default:
        return "📋";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Agora mesmo";
    if (diffMins < 60) return `${diffMins}min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;

    return date.toLocaleDateString("pt-BR");
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-20 bg-gray-200 rounded-lg"></div>
          </div>
        ))}
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
        <p>Nenhum alerta encontrado! 🎉</p>
        <p className="text-sm">Tudo funcionando perfeitamente.</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-3">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`p-4 border rounded-lg transition-all hover:shadow-md ${
              alert.isResolved
                ? "opacity-60 bg-gray-50"
                : alert.severity === "critical"
                  ? "border-red-200 bg-red-50"
                  : alert.severity === "warning"
                    ? "border-yellow-200 bg-yellow-50"
                    : "border-blue-200 bg-blue-50"
            } ${
              !alert.isRead
                ? "border-l-4 " +
                  (alert.severity === "critical"
                    ? "border-l-red-500"
                    : alert.severity === "warning"
                      ? "border-l-yellow-500"
                      : "border-l-blue-500")
                : ""
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="flex items-center gap-2 mt-0.5">
                  {getAlertIcon(alert.severity)}
                  <span className="text-sm">
                    {getCategoryIcon(alert.category)}
                  </span>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{alert.title}</span>
                    <Badge
                      variant="outline"
                      className={getAlertBadgeColor(alert.severity)}
                    >
                      {alert.severity === "critical"
                        ? "Crítico"
                        : alert.severity === "warning"
                          ? "Aviso"
                          : "Info"}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {alert.integrationName}
                    </Badge>
                    {alert.isResolved && (
                      <Badge
                        variant="outline"
                        className="bg-green-100 text-green-800"
                      >
                        Resolvido
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-2">
                    {alert.message}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatTimestamp(alert.timestamp)}</span>
                    </div>
                    {alert.metadata && (
                      <div className="flex gap-3">
                        {alert.metadata.healthScore && (
                          <span>Score: {alert.metadata.healthScore}/100</span>
                        )}
                        {(alert.metadata.issueCount ?? 0) > 0 && (
                          <span>{alert.metadata.issueCount} problema(s)</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-1 ml-4">
                {!alert.isRead && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => onMarkAsRead(alert.id, e)}
                    className="h-8 w-8 p-0"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                )}

                {!alert.isResolved && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => onMarkAsResolved(alert.id, e)}
                    className="h-8 w-8 p-0"
                  >
                    <CheckCircle className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>

            {/* Ações do alerta */}
            {alert.actions.length > 0 && !alert.isResolved && (
              <div className="flex gap-2 mt-3 pt-3 border-t">
                {alert.actions.slice(0, 3).map((action) => (
                  <Button
                    key={action.id}
                    size="sm"
                    variant="outline"
                    onClick={(e) => onExecuteAction(alert.id, action.id, e)}
                    className="text-xs"
                  >
                    <Zap className="h-3 w-3 mr-1" />
                    {action.label}
                  </Button>
                ))}
                {alert.actions.length > 3 && (
                  <Button size="sm" variant="outline" className="text-xs">
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default AlertsCenter;
